import React, { useState, useEffect } from 'react';
import { reportsApi } from '../../services/api';
import { fmt } from '../../utils/billing';
import { downloadGstExcel, downloadGstPdf, hotelInfoFrom, toDDMMYYYY } from '../../utils/gstExport';
import { useNotify } from '../../context/ConfirmContext';

export default function ReportsPage({ hotel }) {
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' | 'gst' | 'shifts' | 'occupancy'
  
  // Date Presets
  const getTodayStr = () => new Date().toLocaleDateString('en-CA');
  const getFirstDayOfMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };

  const [datePreset, setDatePreset] = useState('month'); // 'today' | 'yesterday' | 'week' | 'month' | 'custom'
  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getTodayStr());

  const [summaryData, setSummaryData] = useState(null);
  const [gstData, setGstData] = useState(null);
  const [shiftsData, setShiftsData] = useState([]);
  const [occupancyData, setOccupancyData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Apply Date Presets
  const applyPreset = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    if (preset === 'today') {
      const s = today.toLocaleDateString('en-CA');
      setStartDate(s);
      setEndDate(s);
    } else if (preset === 'yesterday') {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      const s = y.toLocaleDateString('en-CA');
      setStartDate(s);
      setEndDate(s);
    } else if (preset === 'week') {
      const w = new Date(today);
      w.setDate(w.getDate() - 7);
      setStartDate(w.toLocaleDateString('en-CA'));
      setEndDate(today.toLocaleDateString('en-CA'));
    } else if (preset === 'month') {
      setStartDate(getFirstDayOfMonth());
      setEndDate(today.toLocaleDateString('en-CA'));
    }
  };

  useEffect(() => {
    if (hotel?.id) {
      loadReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.id, startDate, endDate]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [sumRes, gstRes, shiftsRes, occRes] = await Promise.all([
        reportsApi.summary(hotel.id, startDate, endDate),
        reportsApi.gst(hotel.id, startDate, endDate),
        reportsApi.shifts(hotel.id, startDate, endDate),
        reportsApi.occupancy(hotel.id, startDate, endDate)
      ]);
      setSummaryData(sumRes);
      setGstData(gstRes);
      setShiftsData(shiftsRes || []);
      setOccupancyData(occRes || []);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
    setLoading(false);
  };

  // GST bill export (Excel / PDF) — open to every role that can see this hotel's
  // reports, not just Super Admin.
  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null
  const exportGst = async (format) => {
    if (!gstData || !gstData.invoices || gstData.invoices.length === 0) {
      await notify('No GST invoices in the selected period.');
      return;
    }
    setExporting(format);
    try {
      const rows = gstData.invoices.map(inv => ({
        invoiceNumber: inv.invoiceNumber,
        date: inv.date,
        guestName: inv.guestName,
        company: inv.company,
        companyGst: inv.companyGst,
        room: inv.room,
        taxable: inv.taxableAmount,
        cgst: inv.cgst,
        sgst: inv.sgst,
        totalGst: inv.totalGst,
        total: inv.totalAmount,
      }));
      const args = {
        rows,
        hotelInfo: hotelInfoFrom(hotel),
        periodLabel: `${toDDMMYYYY(startDate)} to ${toDDMMYYYY(endDate)}`,
        fileName: `GST_Bills_${hotel.name}_${startDate}_to_${endDate}`,
      };
      if (format === 'pdf') downloadGstPdf(args);
      else await downloadGstExcel(args);
    } catch (err) {
      console.error('GST export failed:', err);
      await notify('Could not create the GST file: ' + (err.message || 'unknown error'));
    }
    setExporting(null);
  };

  const exportButtons = (size = '') => (
    <>
      <button className={`btn ${size} btn-ghost`} style={{ fontWeight: 800, border: '1px solid #16a34a', color: '#16a34a' }} disabled={!!exporting} onClick={() => exportGst('excel')}>
        {exporting === 'excel' ? '⌛ Preparing…' : '📊 Download Excel'}
      </button>
      <button className={`btn ${size} btn-ghost`} style={{ fontWeight: 800, border: '1px solid var(--accent)', color: 'var(--accent)' }} disabled={!!exporting} onClick={() => exportGst('pdf')}>
        {exporting === 'pdf' ? '⌛ Preparing…' : '📄 Download PDF'}
      </button>
    </>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header & Date Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: 0 }}>📊 Reports & Business Analytics</h1>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            Revenue, GST tax reports, shift drawer reconciliations, and occupancy metrics.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>GST Bills:</span>
          {exportButtons()}
        </div>
      </div>

      {/* Date Filter Bar */}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 20px', marginBottom: 24, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginRight: 6 }}>Date Range:</span>
          {['today', 'yesterday', 'week', 'month'].map(p => (
            <button
              key={p}
              onClick={() => applyPreset(p)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: '1px solid var(--border)', cursor: 'pointer',
                background: datePreset === p ? 'var(--accent)' : 'white',
                color: datePreset === p ? 'white' : 'var(--text)'
              }}
            >
              {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'week' ? 'Last 7 Days' : 'This Month'}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>From:</label>
          <input
            type="date"
            value={startDate}
            onChange={e => { setDatePreset('custom'); setStartDate(e.target.value); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }}
          />
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>To:</label>
          <input
            type="date"
            value={endDate}
            onChange={e => { setDatePreset('custom'); setEndDate(e.target.value); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }}
          />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', marginBottom: 24 }}>
        <button
          onClick={() => setActiveTab('summary')}
          style={{
            padding: '10px 20px', fontWeight: 700, fontSize: 13, background: 'none', border: 'none',
            color: activeTab === 'summary' ? 'var(--accent)' : 'var(--muted)',
            borderBottom: activeTab === 'summary' ? '3px solid var(--accent)' : '3px solid transparent',
            cursor: 'pointer', marginBottom: -2
          }}
        >
          📊 Executive Overview
        </button>

        <button
          onClick={() => setActiveTab('gst')}
          style={{
            padding: '10px 20px', fontWeight: 700, fontSize: 13, background: 'none', border: 'none',
            color: activeTab === 'gst' ? 'var(--accent)' : 'var(--muted)',
            borderBottom: activeTab === 'gst' ? '3px solid var(--accent)' : '3px solid transparent',
            cursor: 'pointer', marginBottom: -2
          }}
        >
          🏛️ GST & Tax Report
        </button>

        <button
          onClick={() => setActiveTab('shifts')}
          style={{
            padding: '10px 20px', fontWeight: 700, fontSize: 13, background: 'none', border: 'none',
            color: activeTab === 'shifts' ? 'var(--accent)' : 'var(--muted)',
            borderBottom: activeTab === 'shifts' ? '3px solid var(--accent)' : '3px solid transparent',
            cursor: 'pointer', marginBottom: -2
          }}
        >
          💵 Shift Drawer Reconciliation ({shiftsData.length})
        </button>

        <button
          onClick={() => setActiveTab('occupancy')}
          style={{
            padding: '10px 20px', fontWeight: 700, fontSize: 13, background: 'none', border: 'none',
            color: activeTab === 'occupancy' ? 'var(--accent)' : 'var(--muted)',
            borderBottom: activeTab === 'occupancy' ? '3px solid var(--accent)' : '3px solid transparent',
            cursor: 'pointer', marginBottom: -2
          }}
        >
          🛋️ Room Performance & Occupancy
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>Calculating report metrics...</div>
      ) : (
        <>
          {/* TAB 1: EXECUTIVE OVERVIEW */}
          {activeTab === 'summary' && summaryData && (
            <div>
              {/* Stat Cards Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>TOTAL COLLECTED REVENUE</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: '#27ae60', marginTop: 6 }}>{fmt(summaryData.metrics.totalCollected)}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{summaryData.metrics.totalBookings} bookings ({summaryData.metrics.totalNightsSold} nights)</div>
                </div>

                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>OCCUPANCY RATE</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: 'var(--text)', marginTop: 6 }}>{summaryData.metrics.occupancyRate}%</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>RevPAR: {fmt(summaryData.metrics.revPar)}</div>
                </div>

                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>TOTAL EXPENSES</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: 'var(--danger)', marginTop: 6 }}>{fmt(summaryData.metrics.totalExpenses)}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Petty cash & operations</div>
                </div>

                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>NET PROFIT</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: summaryData.metrics.netProfit >= 0 ? 'var(--accent)' : 'var(--danger)', marginTop: 6 }}>
                    {fmt(summaryData.metrics.netProfit)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Total Revenue minus Expenses</div>
                </div>
              </div>

              {/* Breakdown Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Revenue Breakdown */}
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 800, marginTop: 0, marginBottom: 16 }}>Revenue & Charges Breakdown</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                      <span style={{ color: 'var(--muted)' }}>Room Stay Base Tariff:</span>
                      <strong style={{ color: 'var(--text)' }}>{fmt(summaryData.metrics.totalStayRevenue)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                      <span style={{ color: 'var(--muted)' }}>Extra Charges (Services / Food):</span>
                      <strong style={{ color: 'var(--text)' }}>{fmt(summaryData.metrics.totalExtraCharges)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                      <span style={{ color: 'var(--muted)' }}>Damage Charges Collected:</span>
                      <strong style={{ color: 'var(--text)' }}>{fmt(summaryData.metrics.totalDamageCharges)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                      <span style={{ color: 'var(--muted)' }}>GST Collected (5%):</span>
                      <strong style={{ color: '#27ae60' }}>{fmt(summaryData.metrics.totalGstCollected)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                      <span style={{ color: 'var(--muted)' }}>Owner Cash Handovers:</span>
                      <strong style={{ color: '#d35400' }}>{fmt(summaryData.metrics.totalOwnerPickups)}</strong>
                    </div>
                  </div>
                </div>

                {/* Payment Methods Split */}
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 800, marginTop: 0, marginBottom: 16 }}>Payment Mode Breakdown</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
                    {Object.entries(summaryData.paymentSplit).map(([mode, amt]) => (
                      <div key={mode} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                        <span style={{ color: 'var(--muted)', textTransform: 'capitalize' }}>
                          {mode === 'cash' ? '💵 Cash' : mode === 'upi' ? '📱 UPI' : mode === 'credit_card' ? '💳 Credit Card' : mode === 'debit_card' ? '💳 Debit Card' : mode === 'bank_transfer' ? '🏦 Bank Transfer' : mode}:
                        </span>
                        <strong style={{ color: 'var(--text)' }}>{fmt(amt)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: GST & TAX REPORT */}
          {activeTab === 'gst' && gstData && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', background: 'var(--s1)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ fontSize: 14, color: 'var(--text)' }}>Official GST Invoices ({gstData.summary.invoiceCount})</strong>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>Consolidated CGST 2.5% & SGST 2.5% table for monthly tax filing</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>{exportButtons('btn-sm')}</div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase' }}>
                    <th style={{ padding: '12px 14px' }}>Invoice No</th>
                    <th style={{ padding: '12px 14px' }}>Date</th>
                    <th style={{ padding: '12px 14px' }}>Guest Name</th>
                    <th style={{ padding: '12px 14px' }}>Company & GST</th>
                    <th style={{ padding: '12px 14px' }}>Taxable Stay (₹)</th>
                    <th style={{ padding: '12px 14px' }}>CGST 2.5% (₹)</th>
                    <th style={{ padding: '12px 14px' }}>SGST 2.5% (₹)</th>
                    <th style={{ padding: '12px 14px' }}>Total GST (₹)</th>
                    <th style={{ padding: '12px 14px' }}>Total Invoice (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {gstData.invoices.map(inv => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 800 }}>#{inv.invoiceNumber}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--muted)' }}>{toDDMMYYYY(inv.date)}</td>
                      <td style={{ padding: '12px 14px', fontWeight: 600 }}>{inv.guestName}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--muted)', fontSize: 11 }}>
                        {inv.company !== '—' ? <div><strong>{inv.company}</strong></div> : null}
                        {inv.companyGst !== '—' ? <div>{inv.companyGst}</div> : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: 700 }}>{fmt(inv.taxableAmount)}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--muted)' }}>{fmt(inv.cgst)}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--muted)' }}>{fmt(inv.sgst)}</td>
                      <td style={{ padding: '12px 14px', fontWeight: 700, color: '#27ae60' }}>{fmt(inv.totalGst)}</td>
                      <td style={{ padding: '12px 14px', fontWeight: 800, color: 'var(--accent)' }}>{fmt(inv.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f1f5f9', fontWeight: 800, fontSize: 13, borderTop: '2px solid var(--border)' }}>
                    <td colSpan="4" style={{ padding: '14px 14px', textTransform: 'uppercase' }}>GRAND TOTALS ({gstData.summary.invoiceCount} Invoices)</td>
                    <td style={{ padding: '14px 14px' }}>{fmt(gstData.summary.grandTaxableAmount)}</td>
                    <td style={{ padding: '14px 14px' }}>{fmt(gstData.summary.grandCgstAmount)}</td>
                    <td style={{ padding: '14px 14px' }}>{fmt(gstData.summary.grandSgstAmount)}</td>
                    <td style={{ padding: '14px 14px', color: '#27ae60' }}>{fmt(gstData.summary.grandTotalGst)}</td>
                    <td style={{ padding: '14px 14px', color: 'var(--accent)' }}>{fmt(gstData.summary.grandTotalAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* TAB 3: SHIFT DRAWER RECONCILIATION */}
          {activeTab === 'shifts' && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', background: 'var(--s1)', borderBottom: '1px solid var(--border)' }}>
                <strong style={{ fontSize: 14, color: 'var(--text)' }}>Shift Drawer History & Reconciliations</strong>
              </div>

              {shiftsData.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>No shift records found for selected period.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase' }}>
                      <th style={{ padding: '12px 14px' }}>Staff & Shift</th>
                      <th style={{ padding: '12px 14px' }}>Start Time</th>
                      <th style={{ padding: '12px 14px' }}>End Time</th>
                      <th style={{ padding: '12px 14px' }}>Opening Float (₹)</th>
                      <th style={{ padding: '12px 14px' }}>Cash Collections (₹)</th>
                      <th style={{ padding: '12px 14px' }}>UPI Collections (₹)</th>
                      <th style={{ padding: '12px 14px' }}>Handed to Owner (₹)</th>
                      <th style={{ padding: '12px 14px' }}>Closing Cash (₹)</th>
                      <th style={{ padding: '12px 14px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shiftsData.map(s => (
                      <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 14px', fontWeight: 700 }}>
                          {s.user_display_name || s.user_name || 'Staff'}
                          <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>{s.user_shift || 'Shift'}</div>
                        </td>
                        <td style={{ padding: '12px 14px', color: 'var(--muted)' }}>{new Date(s.start_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td style={{ padding: '12px 14px', color: 'var(--muted)' }}>{s.end_at ? new Date(s.end_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Active'}</td>
                        <td style={{ padding: '12px 14px' }}>{fmt(s.opening_cash || 0)}</td>
                        <td style={{ padding: '12px 14px', fontWeight: 700, color: '#27ae60' }}>{fmt(s.collected_cash || 0)}</td>
                        <td style={{ padding: '12px 14px', fontWeight: 700, color: '#2980b9' }}>{fmt(s.collected_upi || 0)}</td>
                        <td style={{ padding: '12px 14px', color: '#d35400' }}>{fmt(s.owner_pickups || 0)}</td>
                        <td style={{ padding: '12px 14px', fontWeight: 800 }}>{fmt(s.closing_cash || 0)}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 4, background: s.status === 'active' ? 'rgba(46,204,113,0.12)' : 'rgba(149,165,166,0.12)', color: s.status === 'active' ? '#27ae60' : 'var(--muted)', fontWeight: 800, fontSize: 10 }}>
                            {s.status ? s.status.toUpperCase() : 'COMPLETED'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* TAB 4: ROOM OCCUPANCY & PERFORMANCE */}
          {activeTab === 'occupancy' && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', background: 'var(--s1)', borderBottom: '1px solid var(--border)' }}>
                <strong style={{ fontSize: 14, color: 'var(--text)' }}>Room Performance by Category</strong>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase' }}>
                    <th style={{ padding: '12px 16px' }}>Room Type</th>
                    <th style={{ padding: '12px 16px' }}>Rooms in Hotel</th>
                    <th style={{ padding: '12px 16px' }}>Bookings Count</th>
                    <th style={{ padding: '12px 16px' }}>Room Nights Sold</th>
                    <th style={{ padding: '12px 16px' }}>Revenue Generated (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {occupancyData.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '14px 16px', fontWeight: 800 }}>{row.roomType}</td>
                      <td style={{ padding: '14px 16px' }}>{row.totalRooms} rooms</td>
                      <td style={{ padding: '14px 16px', fontWeight: 700 }}>{row.bookingCount} bookings</td>
                      <td style={{ padding: '14px 16px', fontWeight: 700 }}>{row.totalNightsSold} nights</td>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: 'var(--accent)' }}>{fmt(row.totalRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
